import { Button, DialogActions, DialogContent, DialogTitle, Grid, Radio, RadioGroup } from "@mui/material";
import { renderClose } from "../../common/dialog";
import { useState } from "react";
import { createEntityStore, UseEntityStore } from "../../common/entityStore";
import { propTextField } from "../../common/entityStoreForm";
import { KeysOfType } from "../../common/utils";
import { propIf, propLengthMax } from "../../common/entityStoreValidators";
import { CustomerFlagId } from "../../common/enum";
import { createAxios } from "../../common/axios";
import { useSimpleDialog } from "../../common/dialog";

export const RejectedImportCustomerDialog = (props: {
    close: () => void;
    item: RejectedImportItemDto;
    customer: RejectedImportCustomerDto;
    onSaved: (message: string) => void;
}) => {
    const [useMergeStore] = useState(() => createEntityStore<MergeData>({
        path: "reports/rejectedImportItems/processExistingCustomer", id: null, fetchOnInit: false,
        saveProps: ["customerId", "title", "fname", "lname", "strt1", "strt2", "coadr", "zipc", "city", "tel", "mob", "email"],
        data: {
            customerId: props.customer.id,
            itemTitle: props.item.title, customerTitle: props.customer.title, useItemTitle: props.item.title.length > 0,
            itemFname: props.item.fname, customerFname: props.customer.fname, useItemFname: props.item.fname.length > 0,
            itemLname: props.item.lname, customerLname: props.customer.lname, useItemLname: props.item.lname.length > 0,
            itemStrt1: props.item.strt1, customerStrt1: props.customer.strt1, useItemStrt1: props.item.strt1.length > 0,
            itemStrt2: props.item.strt2, customerStrt2: props.customer.strt2, useItemStrt2: props.item.strt2.length > 0,
            itemCoadr: props.item.coadr, customerCoadr: props.customer.coadr, useItemCoadr: props.item.coadr.length > 0,
            itemZipc: props.item.zipc, customerZipc: props.customer.zipc, useItemZipc: props.item.zipc.length > 0,
            itemCity: props.item.city, customerCity: props.customer.city, useItemCity: props.item.city.length > 0,
            itemTel: props.item.tel, customerTel: props.customer.tel, useItemTel: props.item.tel.length > 0,
            itemMob: props.item.mob, customerMob: props.customer.mob, useItemMob: props.item.mob.length > 0,
            itemEmail: props.item.email, customerEmail: props.customer.email, useItemEmail: props.item.email.length > 0,
        },
        onSaved(data) {
            props.onSaved(data.message!);
            props.close();
        },
        propValidators: [
            propIf(p => p.useItemTitle, () => propLengthMax("itemTitle", 50)),
            propIf(p => p.useItemFname, () => propLengthMax("itemFname", 50)),
            propIf(p => p.useItemLname, () => propLengthMax("itemLname", 50)),
            propIf(p => p.useItemStrt1, () => propLengthMax("itemStrt1", 50)),
            propIf(p => p.useItemStrt2, () => propLengthMax("itemStrt2", 50)),
            propIf(p => p.useItemCoadr, () => propLengthMax("itemCoadr", 50)),
            propIf(p => p.useItemZipc, () => propLengthMax("itemZipc", 5)),
            propIf(p => p.useItemCity, () => propLengthMax("itemCity", 50)),
            propIf(p => p.useItemTel, () => propLengthMax("itemTel", 50)),
            propIf(p => p.useItemMob, () => propLengthMax("itemMob", 50)),
            propIf(p => p.useItemEmail, () => propLengthMax("itemEmail", 50)),
        ],
    }));

    const simpleDialog = useSimpleDialog();

    async function checkOrder(customerId: number) {
        try {
            const { data } = await createAxios().post("reports/rejectedImportItems/checkOrder", { itemId: customerId });
            return data;
        } catch {
            return null;
        }
    }
    async function save() {
        const store = useMergeStore.getState();
        const data = { ...store.clientData };

        var result = await checkOrder(data.customerId);

        if (!result?.isValid) {

            simpleDialog.show("Result", result?.message ?? "Error occurred during calling API. Please try it again later.");
            return;
        }

        data.title = data.useItemTitle ? data.itemTitle : data.customerTitle;
        data.fname = data.useItemFname ? data.itemFname : data.customerFname;
        data.lname = data.useItemLname ? data.itemLname : data.customerLname;
        data.strt1 = data.useItemStrt1 ? data.itemStrt1 : data.customerStrt1;
        data.strt2 = data.useItemStrt2 ? data.itemStrt2 : data.customerStrt2;
        data.coadr = data.useItemCoadr ? data.itemCoadr : data.customerCoadr;
        data.zipc = data.useItemZipc ? data.itemZipc : data.customerZipc;
        data.city = data.useItemCity ? data.itemCity : data.customerCity;
        data.tel = data.useItemTel ? data.itemTel : data.customerTel;
        data.mob = data.useItemMob ? data.itemMob : data.customerMob;
        data.email = data.useItemEmail ? data.itemEmail : data.customerEmail;

        store.setClientData(data);
        store.save();
    }

    const { isSaveDisabled } = useMergeStore(p => ({ isSaveDisabled: p.isSaveDisabled }));

    return <>
        <DialogTitle>Attach to existing customer</DialogTitle>
        {simpleDialog.render()}
        {renderClose(props.close)}
        <DialogContent>
            <Grid container>
                <Grid item container columnSpacing={2}>
                    <Grid item xs>Rejected values</Grid>
                    <Grid item width={85}></Grid>
                    <Grid item xs>Existing values</Grid>
                    <Grid item xs>Chosen values</Grid>
                </Grid>
                <CustomerRow itemProp="itemTitle" customerProp="customerTitle" useItemValueProp="useItemTitle" label="Title" useMergeStore={useMergeStore} />
                <CustomerRow itemProp="itemFname" customerProp="customerFname" useItemValueProp="useItemFname" label="First name" useMergeStore={useMergeStore} />
                <CustomerRow itemProp="itemLname" customerProp="customerLname" useItemValueProp="useItemLname" label="Last name" useMergeStore={useMergeStore} />
                <CustomerRow itemProp="itemStrt1" customerProp="customerStrt1" useItemValueProp="useItemStrt1" label="Street" useMergeStore={useMergeStore} />
                <CustomerRow itemProp="itemStrt2" customerProp="customerStrt2" useItemValueProp="useItemStrt2" label="House number" useMergeStore={useMergeStore} />
                <CustomerRow itemProp="itemCoadr" customerProp="customerCoadr" useItemValueProp="useItemCoadr" label="C/O address" useMergeStore={useMergeStore} />
                <CustomerRow itemProp="itemZipc" customerProp="customerZipc" useItemValueProp="useItemZipc" label="ZIP code" useMergeStore={useMergeStore} />
                <CustomerRow itemProp="itemCity" customerProp="customerCity" useItemValueProp="useItemCity" label="City" useMergeStore={useMergeStore} />
                <CustomerRow itemProp="itemTel" customerProp="customerTel" useItemValueProp="useItemTel" label="Phone" useMergeStore={useMergeStore} />
                <CustomerRow itemProp="itemMob" customerProp="customerMob" useItemValueProp="useItemMob" label="Mobile" useMergeStore={useMergeStore} />
                <CustomerRow itemProp="itemEmail" customerProp="customerEmail" useItemValueProp="useItemEmail" label="Email" useMergeStore={useMergeStore} />
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={save} disabled={isSaveDisabled} color="save">Save</Button>
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
};

function CustomerRow({ itemProp, customerProp, useItemValueProp, label, useMergeStore }: {
    itemProp: KeysOfType<MergeData, string | null | undefined>;
    customerProp: KeysOfType<MergeData, string | null | undefined>;
    useItemValueProp: KeysOfType<MergeData, boolean>;
    label: string;
    useMergeStore: UseEntityStore<MergeData>;
}) {
    const { useItemValue, setClientDataProp } = useMergeStore(p => ({
        useItemValue: p.clientData[useItemValueProp], setClientDataProp: p.setClientDataProp
    }));
    return <Grid item container columnSpacing={2}>
        <Grid item xs>{propTextField(useMergeStore)(itemProp, label)}</Grid>
        <Grid item>
            <RadioGroup row value={useItemValue} onChange={p => setClientDataProp(useItemValueProp, p.target.value === "true")}>
                <Radio value={true} />
                <Radio value={false} />
            </RadioGroup>
        </Grid>
        <Grid item xs>{propTextField(useMergeStore)(customerProp, label, { disabled: true })}</Grid>
        <Grid item xs>{propTextField(useMergeStore)(useItemValue ? itemProp : customerProp, label, { disabled: true })}</Grid>
    </Grid>;
}

export interface RejectedImportItemDto extends RejectedImportCustomerDtoBase {
    rejectDate: string;
    rejectReason: string;
    isResolved: boolean;
    customers: RejectedImportCustomerDto[];    
}

export interface RejectedImportCustomerDto extends RejectedImportCustomerDtoBase {
    id: number;
    customerId: number;
    customerStateId: number;
    emailStatusId: CustomerFlagId;
    mailStatusId: CustomerFlagId;
    telStatusId: CustomerFlagId;
    mobStatusId: CustomerFlagId;
    emailStopSystemId: CustomerFlagId;
    loserStatusId: CustomerFlagId;
}

export interface RejectedImportCustomerDtoBase {
    id: number;
    custId: string;
    title: string;
    fname: string;
    lname: string;
    strt1: string;
    strt2: string;
    coadr: string;
    zipc: string;
    city: string;
    adrState: string;
    tel: string;
    mob: string;
    email: string;
    sexTypeId: number;

    emailStopId: CustomerFlagId;
    mailStopId: CustomerFlagId;
    phoneStopId: CustomerFlagId;

    sc1: string;
    sc2: string;
}

interface MergeData {
    customerId: number;
    itemTitle: string; customerTitle: string; useItemTitle: boolean; title?: string;
    itemFname: string; customerFname: string; useItemFname: boolean; fname?: string;
    itemLname: string; customerLname: string; useItemLname: boolean; lname?: string;
    itemStrt1: string; customerStrt1: string; useItemStrt1: boolean; strt1?: string;
    itemStrt2: string; customerStrt2: string; useItemStrt2: boolean; strt2?: string;
    itemCoadr: string; customerCoadr: string; useItemCoadr: boolean; coadr?: string;
    itemZipc: string; customerZipc: string; useItemZipc: boolean; zipc?: string;
    itemCity: string; customerCity: string; useItemCity: boolean; city?: string;
    itemTel: string; customerTel: string; useItemTel: boolean; tel?: string;
    itemMob: string; customerMob: string; useItemMob: boolean; mob?: string;
    itemEmail: string; customerEmail: string; useItemEmail: boolean; email?: string;

    message?: string;
}
